import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from '@/components/ui/Container';
import Button from '@/components/ui/Button';

interface FooterProps {
  showCta?: boolean;
}

export default function Footer({ showCta = true }: FooterProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900">
      {/* CTA Section - only shown on landing/public pages */}
      {showCta && (
        <div className="py-16 md:py-20">
          <Container>
            <div className="relative bg-gradient-to-r from-mk-pink-600 to-mk-pink-500 rounded-3xl p-8 md:p-12 text-center overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-1/2 -right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-1/2 -left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              </div>

              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {t('footer.cta.title')}
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  {t('footer.cta.subtitle')}
                </p>
                <Button href="/login" variant="white" className="animate-pulse-subtle">
                  {t('footer.cta.button')}
                </Button>
              </div>
            </div>
          </Container>
        </div>
      )}

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <Container>
          <div className="py-8 flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="text-white font-bold text-xl">
              MyPink<span className="text-mk-pink-400">Contact</span>
            </Link>

            {/* Copyright */}
            <p className="text-gray-400 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>

            {/* Links */}
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.links.privacy')}
              </Link>
              <Link to="/legal" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.links.legal')}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.links.terms')}
              </Link>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="pb-6">
            <p className="text-gray-500 text-xs text-center max-w-3xl mx-auto">
              {t('footer.disclaimer')}
            </p>
          </div>
        </Container>
      </div>
    </footer>
  );
}
